// Status filter: three independent checkboxes, not one choice of three.
//
// His requirement, literal: "Change status drop-down from 'On Market, Sold' to 'Active, Pending,
// Sold' (Splitting 'On Market' to two statuses: 'Active' and 'Pending')" and "Add check boxes to
// Status field". `All` is a display value shown when all three are ticked, not an item in the list
// — his answer OC 2, the same shape `Custom` has in the Show field.
export const STATUS_VALUES = ['Active', 'Pending', 'Sold']

// Persisted as JSON inside the existing String field, and the format is the whole reason this
// module exists.
//
// arv.filters.status in reilm-be modules/properties/model.js is a String, and arv.filters is a
// fixed set of seven keys — a fourth shape means a schema change this MR does not carry. A
// comma-joined list would fit that String, and it is exactly what cannot be used: the old dropdown
// wrote the bare word `Active`, and the new one writes the bare word `Active` when the user ticks
// Active alone. Those two mean different sets. Counted read-only against production 26.08: 27 saved
// filter sets, `All` 12, `Sold` 10, `Active` 5 — so five documents sit precisely on that collision.
// JSON has no such overlap: a legacy value never parses as an array, and every new value does.
//
// The legacy readings are what the old code did, not a guess. `Active` ran
// `if (f.status === 'Active' && c.status?.toLowerCase() === 'sold') return false` — it excluded Sold
// and kept everything else, so under the split it is Active plus Pending. `Sold` kept only Sold.
// `All`, an absent value and anything unrecognised are the full set.
export function parseStatusFilter(stored) {
  if (typeof stored === 'string' && stored.startsWith('[')) {
    try {
      const parsed = JSON.parse(stored)
      // Filtered through STATUS_VALUES rather than trusted: it fixes the order (so the checkbox
      // rows and the stored value cannot disagree about it) and drops anything not a status.
      if (Array.isArray(parsed)) return STATUS_VALUES.filter((v) => parsed.includes(v))
    } catch {
      // Not JSON after all — fall through to the legacy readings below rather than throwing on a
      // value a user's saved filter set is allowed to hold.
    }
  }
  if (stored === 'Sold') return ['Sold']
  if (stored === 'Active') return ['Active', 'Pending']
  return [...STATUS_VALUES]
}

export function serialiseStatusFilter(selected) {
  return JSON.stringify(STATUS_VALUES.filter((v) => selected.includes(v)))
}

// What the collapsed field shows. `All` is the only one of these the design draws
// (files/sow2/spec/arv.md section 4, frame `statuses (new)/btn_54`); the field is 99px wide, which
// is about ten characters at 14px, so two names side by side do not fit and a truncated `Active,
// Pen…` would be worse than a count. One name fits and is more informative than "1 selected".
export function statusFilterLabel(selected) {
  if (selected.length === STATUS_VALUES.length) return 'All'
  if (selected.length === 0) return 'None'
  if (selected.length === 1) return selected[0]
  return `${selected.length} selected`
}

// All three ticked means NO status check, not "is a member of the set", and the difference is a
// comp whose status is missing or something we have not seen. The old `All` branch ran no check at
// all and such a comp passed; strict membership would drop it silently, which is a filter deciding
// on the user's behalf that an unknown status is the wrong one. Nothing else on this screen removes
// a row without the user asking.
export function statusMatches(comp, selected) {
  if (selected.length === STATUS_VALUES.length) return true
  const status = String(comp?.status ?? '').toLowerCase()
  return selected.some((v) => v.toLowerCase() === status)
}
